'use client'

import {
    Consultation,
    CONSULTATION_STATUS_COLORS,
    CONSULTATION_STATUS_LABELS,
    getConsultationsByClient,
} from '@/lib/api/consultations/consultation-api'
import { setProntuarioOrigin } from '@/lib/prontuario/prontuario-origin'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdArrowForward,
    MdEventNote,
    MdLocalHospital,
    MdPerson,
    MdReplay,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { RecordActionsMenu } from '@/components/medical/record-actions-menu'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function ConsultasPage() {
    const { client, clientId } = useProntuarioData()
    const navigate = useProntuarioNavigate()

    const [consultations, setConsultations] = useState<Consultation[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (clientId) {
            loadConsultations()
        }
    }, [clientId])

    const loadConsultations = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getConsultationsByClient(
                parseInt(clientId, 10)
            )
            if (response.success) {
                setConsultations(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar consultas')
            }
        } catch (error) {
            toast.error('Erro ao carregar consultas')
        } finally {
            setLoading(false)
        }
    }

    const getConsultationDisplayName = (consultation: Consultation): string => {
        return (
            consultation.catalog?.name ||
            consultation.title ||
            `Consulta #${consultation.id}`
        )
    }

    /**
     * A consulta não guarda `finalPrice` — o valor vive na cobrança. Quando não há
     * cobrança, a linha simplesmente não mostra valor.
     */
    const formatCentsLabel = (cents: number): string => {
        return `R$ ${(cents / 100).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando consultas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Consultas
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Histórico de consultas do paciente
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-1"
                        onClick={() => {
                            // Guarda a volta ANTES de sair: a partir daqui o
                            // operador atravessa agendamento, cobrança e detalhe —
                            // telas que não sabem de onde ele veio.
                            setProntuarioOrigin(
                                `/person/clients/${clientId}/prontuario/consultas`,
                                client?.name || 'paciente'
                            )
                            navigate(
                                `/atendimento/scheduling?type=CONSULTATION&clientId=${clientId}`
                            )
                        }}
                    >
                        <MdAdd className="text-base" />
                        Nova consulta
                    </button>
                    <span className="badge badge-lg badge-ghost">
                        {consultations.length}{' '}
                        {consultations.length === 1 ? 'consulta' : 'consultas'}
                    </span>
                </div>
            </div>

            {/* Lista */}
            {consultations.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdEventNote className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma consulta registrada
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {consultations.map(consultation => (
                        <div
                            key={consultation.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() =>
                                navigate(
                                    `/bookings/consultations/${consultation.id}`
                                )
                            }
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdEventNote className="text-xl text-secondary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-base-content truncate">
                                                    {getConsultationDisplayName(
                                                        consultation
                                                    )}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {format(
                                                        new Date(
                                                            consultation.scheduledAt ||
                                                                consultation.createdAt
                                                        ),
                                                        "dd/MM/yyyy 'às' HH:mm",
                                                        { locale: ptBR }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span
                                                className={`badge ${
                                                    CONSULTATION_STATUS_COLORS[
                                                        consultation.status
                                                    ]
                                                } text-white`}
                                            >
                                                {
                                                    CONSULTATION_STATUS_LABELS[
                                                        consultation.status
                                                    ]
                                                }
                                            </span>
                                            {consultation.isReturn && (
                                                <span className="badge badge-ghost gap-1">
                                                    <MdReplay className="text-sm" />
                                                    Retorno
                                                </span>
                                            )}
                                            {consultation.healthPlan && (
                                                <span className="badge badge-ghost gap-1">
                                                    <MdLocalHospital className="text-sm" />
                                                    {consultation.healthPlan.name}
                                                </span>
                                            )}
                                        </div>

                                        {consultation.diagnosis && (
                                            <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                                {consultation.diagnosis}
                                            </p>
                                        )}

                                        <div className="flex flex-wrap items-center gap-4 text-xs text-base-content/50">
                                            {consultation.professional && (
                                                <div className="flex items-center gap-1">
                                                    <MdPerson className="text-sm" />
                                                    <span>
                                                        {
                                                            consultation
                                                                .professional
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {consultation.completedAt && (
                                                <div className="flex items-center gap-1">
                                                    <MdAccessTime className="text-sm" />
                                                    <span>
                                                        Concluída:{' '}
                                                        {format(
                                                            new Date(
                                                                consultation.completedAt
                                                            ),
                                                            'dd/MM/yyyy',
                                                            { locale: ptBR }
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {consultation.billing?.amount ? (
                                                <span className="font-semibold text-secondary">
                                                    {formatCentsLabel(
                                                        consultation.billing
                                                            .amount
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="ml-2 flex items-center gap-1">
                                        <RecordActionsMenu
                                            kind="consultation"
                                            id={consultation.id}
                                            clientEmail={client?.email}
                                        />
                                        <span
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Ver detalhes"
                                        >
                                            <MdArrowForward className="text-lg" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
